import { responseMessageFromStatus } from '@/utils/responseHelpers'
import { RESPONSE } from '@/utils/constants'
import { logDev } from '@/utils/env'
import appsignal from '@/appsignal'
import type { Span } from '@appsignal/javascript'
import type { TFunction, ApiError } from '@/types'

type OverrideValue = string | ((ctx: OverrideContext) => string | null)

interface OverrideContext {
  err: ApiError | unknown
  status: number | string | null | undefined
  t: TFunction
}

type Overrides = Record<string, OverrideValue>

interface ErrorMessageOptions {
  overrides?: Overrides
}

interface NotifyOptions {
  type?: string
  duration?: number
  closable?: boolean
  toast?: boolean
}

interface NotifyApiErrorOptions {
  t?: TFunction
  setError?: (msg: string) => void
  showMessage?: (text: string, options?: NotifyOptions) => void
  overrides?: Overrides
  notifyOptions?: NotifyOptions
  /** Extra AppSignal span tags. Never put patient data or filenames in here. */
  tags?: Record<string, string>
  /** Extra AppSignal span params. Never put patient data or filenames in here. */
  params?: Record<string, unknown>
  /** Report even when the status is silent or the error looks network-related. */
  forceReport?: boolean
  /** AppSignal error name. Incidents group per name, so a distinct name per
   *  error code splits them into separate, dashboardable incidents. */
  errorName?: string
  /** AppSignal action, e.g. 'auth.login.password'. Groups incidents per flow. */
  action?: string
}

function pickOverride(overrides: Overrides | undefined, status: number | string | null | undefined, ctx: OverrideContext): string | null {
  if (!overrides) return null
  const key: string | null = status == null ? null : String(status)
  const match: OverrideValue | undefined =
    key != null && Object.prototype.hasOwnProperty.call(overrides, key)
      ? overrides[key]
      : overrides['*']
  if (match == null) return null
  if (typeof match === 'function') return match(ctx)
  if (typeof match === 'string') return ctx.t(match, { defaultValue: match })
  return null
}

export function getErrorMessage(err: ApiError | unknown, t: TFunction, opts: ErrorMessageOptions = {}): string {
  const typedErr = err as ApiError | undefined
  const status: number | string | null | undefined =
    typedErr?.status == RESPONSE.RESP_PRECONDITION_FAILED
      ? (typedErr.data as Record<string, unknown>)?.error as string
      : typedErr?.status ?? typedErr?.response?.status

  logDev('notifyApiError -> status:', status, 'err:', err)

  const overridden = pickOverride(opts.overrides, status, { err, status, t })
  if (overridden) return overridden

  const fromMap = responseMessageFromStatus(t, status)
  if (fromMap) return fromMap

  return t('responses.500', {
    defaultValue: 'Onbekende fout.',
  })
}

const SILENT_STATUSES: Set<number> = new Set([401, 412, 417, 493, 495, 497])

const NETWORK_ERROR_PATTERNS: RegExp[] = [/Load failed/i, /Failed to fetch/i, /NetworkError/i]

/** Copy with a different name, so the caught error itself stays untouched. */
function renameError(err: Error, name: string): Error {
  const copy = new Error(err.message)
  copy.name = name
  copy.stack = err.stack
  return copy
}

function notifyApiError(err: ApiError | unknown, { t, setError, showMessage, overrides, notifyOptions, tags, params, forceReport, errorName, action }: NotifyApiErrorOptions = {}): string {
  const msg: string = getErrorMessage(err, t!, { overrides })

  const typedErr = err as ApiError | undefined
  const errMsg = typedErr?.message || ''
  const isNetworkError = NETWORK_ERROR_PATTERNS.some((p) => p.test(errMsg))
  const isSilent = SILENT_STATUSES.has(typedErr?.status as number) || isNetworkError

  if (appsignal && (forceReport || !isSilent)) {
    const original: Error = err instanceof Error ? err : new Error(String(typedErr?.message || msg))
    const errorToSend: Error = errorName ? renameError(original, errorName) : original
    appsignal.sendError(errorToSend, (span: Span) => {
      if (action) span.setAction(action)
      span.setTags({
        status: String(typedErr?.status ?? 'unknown'),
        path: window.location.pathname,
        ...(tags || {}),
      })
      span.setParams({
        status: typedErr?.status,
        errorData: typedErr?.data,
        url: window.location.href,
        ...(params || {}),
      })
      return span
    })
  }

  if (typeof setError === 'function') setError(msg)

  if (typeof showMessage === 'function') {
    // defaults blijven zoals nu
    const defaults: NotifyOptions = { type: 'error', duration: 6000, closable: true, toast: true }
    showMessage(msg, { ...defaults, ...(notifyOptions || {}) })
  }

  return msg
}

export default notifyApiError
